import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { postMessage } from "../../../../core/message";
import { isAndroidApp, isIOSApp } from "../../../../utils/user-agent";
import FadeInWrapper from "../../../../components/common/fade-in-wrapper";
import { FaHandPointer } from "react-icons/fa";
import MahiIcon from "./image/mahi_ico.png";

// OnboardingLocation의 three-line-ticker에서 사용되는 이미지들 (프리로딩용)
const tickerImages = [
  // images1
  "https://cdn-mahi.com/1748522896323-%E1%84%83%E1%85%A1%E1%84%8B%E1%85%AE%E1%86%AB%E1%84%85%E1%85%A9%E1%84%83%E1%85%B3%20%281%29.jpeg",
  "https://cdn-mahi.com/1748496148615-IMG_1167.jpeg",
  "https://cdn-mahi.com/1748488832401-IMG_0183.png",
  "https://cdn-mahi.com/1748470361337-IMG_0683.jpeg",
  "https://cdn-mahi.com/1748438611757-1000030056.jpg",
  "https://cdn-mahi.com/1748416122087-%E1%84%83%E1%85%A1%E1%84%8B%E1%85%AE%E1%86%AB%E1%84%85%E1%85%A9%E1%84%83%E1%85%B3.jpeg",
  "https://cdn-mahi.com/1748266353171-451812503_4846034074",
  "https://cdn-mahi.com/1748246351668-1000002664.jpg",
  // images2
  "https://cdn-mahi.com/1748245206142-IMG_8475.jpeg",
  "https://cdn-mahi.com/1748247390711-IMG_2177.jpg",
  "https://cdn-mahi.com/1748241223320-470035538_2041886632",
  "https://cdn-mahi.com/1748252922507-IMG_1709.jpeg",
  "https://cdn-mahi.com/1748145217586-IMG_5572.jpeg",
  "https://cdn-mahi.com/1748066375223-IMG_6111.jpeg",
  "https://cdn-mahi.com/1748008290365-image.jpg",
  "https://cdn-mahi.com/1747994753169-%E1%84%83%E1%85%A1%E1%84%8B%E1%85%AE%E1%86%AB%E1%84%85%E1%85%A9%E1%84%83%E1%85%B3.jpeg",
  // images3
  "https://cdn-mahi.com/1747984418768-469824278_5442478754",
  "https://cdn-mahi.com/1747970351232-IMG_8836.jpeg",
  "https://cdn-mahi.com/1747968952123-baker%2520poster1_VSCO",
  "https://cdn-mahi.com/1747889716039-IMG_4820.jpeg",
  "https://cdn-mahi.com/1747820998756-%E1%84%83%E1%85%A1%E1%84%8B%E1%85%AE%E1%86%AB%E1%84%85%E1%85%A9%E1%84%83%E1%85%B3.jpeg",
  "https://cdn-mahi.com/1747798523485-IMG_7961.jpeg",
  "https://cdn-mahi.com/1747639271875-IMG_6568.jpeg",
  "https://cdn-mahi.com/1747400799511-IMG_1711.jpeg",
  "https://cdn-mahi.com/1747401809208-%E1%84%83%E1%85%A1%E1%84%8B%E1%85%AE%E1%86%AB%E1%84%85%E1%85%A9%E1%84%83%E1%85%B3.jpeg",
  "https://cdn-mahi.com/1747383778174-393369832_1123045721",
  "https://cdn-mahi.com/1747379649565-IMG_2343.jpeg",
  "https://cdn-mahi.com/1747370560548-C8AC2816-87B4-4054-B",
  "https://cdn-mahi.com/1747369530584-1000075408.jpg",
  "https://cdn-mahi.com/1747368563674-1000079295.png",
  "https://cdn-mahi.com/1747308354210-481926807_1796299491",
];

export default function NotificationRequestPage() {
  const navigate = useNavigate();
  const [os, setOS] = useState<"android" | "ios" | "web">("web");
  const [permissionRequested, setPermissionRequested] = useState(false);

  // 원본 + 리사이징된 이미지 모두 병렬로 프리로딩
  useEffect(() => {
    const preloadImages = () => {
      const deviceWidth = window.innerWidth > 742 ? 742 : window.innerWidth;
      const width = deviceWidth * 1;
      const dpr = window.devicePixelRatio > 1 ? 2 : 1;

      const allPromises = tickerImages.flatMap((imageSrc) => [
        // 원본 이미지
        new Promise((resolve) => {
          const img = new Image();
          img.onload = resolve;
          img.src = imageSrc;
        }),
        // 리사이징된 이미지
        new Promise((resolve) => {
          const img = new Image();
          img.onload = resolve;
          img.src = `${imageSrc}?w=${width * (dpr >= 2 ? 1.4 : 1)}&q=80`;
        }),
      ]);

      Promise.all(allPromises);
    };

    preloadImages();
  }, []);

  useEffect(() => {
    if (isAndroidApp()) setOS("android");
    else if (isIOSApp()) setOS("ios");
    else setOS("web");
  }, []);

  const handleRequestPermission = () => {
    if (!permissionRequested) {
      // 1단계: 앱(Webview)에 권한 요청 메시지 전송
      postMessage("REQ_NOTIFICATION", "");
      setPermissionRequested(true);
    } else {
      // 2단계: 권한 요청 후 다음 단계로 이동
      navigate("/onboarding/location");
    }
  };

  return (
    <div className="h-full bg-white min-h-screen flex flex-col relative pb-24">
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <FadeInWrapper className="w-full flex flex-col items-center">
          {/* OS별 이미지 또는 아이콘 분기 */}

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-center space-y-3"
          >
            <h1 className="text-2xl font-bold text-gray-900">
              선착순 할인 정보를
              <br />
              가장 먼저 알려드릴게요
            </h1>
          </motion.div>

          {/* 시스템 권한 요청 팝업 시뮬레이션 */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 0.8, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4, ease: "easeOut" }}
            className="mt-8 w-full max-w-[340px] relative pointer-events-none"
          >
            {os === "ios" ? (
              /* iOS Style Alert Dialog */
              <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50">
                {/* App Icon */}
                <div className="flex flex-col items-center pt-6 pb-4 px-6">
                  <div className="w-16 h-16 rounded-2xl shadow-lg mb-3 relative">
                    <img
                      src={MahiIcon}
                      alt="mahi"
                      className="w-full h-full object-cover rounded-2xl"
                    />
                    {/* 뱃지 아이콘 */}
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        delay: 0.8,
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                      }}
                      className="absolute -top-2 -right-2 bg-[#C2FC8E] w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm shadow-md border-2 border-white text-black"
                    >
                      1
                    </motion.div>
                  </div>
                  <h3 className="text-[17px] font-semibold text-center mb-2">
                    "마히"에서 알림을
                    <br />
                    보내고자 합니다
                  </h3>
                  <p className="text-[13px] text-gray-600 text-center leading-relaxed">
                    알림에는 사운드, 아이콘 배지,
                    <br />
                    알림이 포함될 수 있습니다.
                  </p>
                </div>

                {/* Divider */}
                <div className="h-[0.5px] bg-gray-300" />

                {/* Buttons */}
                <div className="flex">
                  <div className="flex-1 py-3.5 text-[17px] text-gray-700 text-center">
                    허용 안 함
                  </div>
                  <div className="w-[0.5px] bg-gray-300" />
                  <div className="relative flex-1">
                    <div className="w-full py-3.5 text-[17px] font-semibold text-blue-500 text-center">
                      허용
                    </div>

                    {/* Hand Pointer Icon */}
                    <motion.div
                      animate={{
                        scale: [1, 1.1, 1],
                        y: [0, -5, 0],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                      className="absolute left-1/2 -bottom-10 -translate-x-1/2 z-20"
                    >
                      <FaHandPointer className="w-8 h-8 text-blue-500" />
                    </motion.div>
                  </div>
                </div>
              </div>
            ) : (
              /* Android Style Permission Dialog */
              <div className="bg-white rounded-lg shadow-2xl border border-gray-100">
                {/* Header */}
                <div className="px-6 pt-6 pb-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg shadow-md flex-shrink-0 relative">
                      <img
                        src={MahiIcon}
                        alt="mahi"
                        className="w-full h-full object-cover rounded-lg"
                      />
                      {/* 뱃지 아이콘 */}
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                          delay: 0.8,
                          type: "spring",
                          stiffness: 300,
                          damping: 20,
                        }}
                        className="absolute -top-1.5 -right-1.5 bg-[#C2FC8E] w-5 h-5 rounded-full flex items-center justify-center font-bold text-[10px] shadow-sm border border-white text-black"
                      >
                        1
                      </motion.div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[16px] font-medium text-gray-900 mb-2">
                        마히에서 알림을 보내도록
                        <br />
                        허용하시겠습니까?
                      </h3>
                      <p className="text-[14px] text-gray-600 leading-relaxed">
                        배지, 소리 및 알림 배너가 표시될 수 있습니다.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex items-center justify-end gap-2 px-6 py-4">
                  <div className="px-4 py-2 text-[14px] font-medium text-gray-700 rounded">
                    거부
                  </div>
                  <div className="relative">
                    <div className="px-4 py-2 text-[14px] font-medium text-blue-600 rounded bg-blue-50">
                      허용
                    </div>

                    {/* Hand Pointer Icon */}
                    <motion.div
                      animate={{
                        scale: [1, 1.1, 1],
                        y: [0, -5, 0],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                      className="absolute left-1/2 -bottom-10 -translate-x-1/2 z-20"
                    >
                      <FaHandPointer className="w-8 h-8 text-blue-600" />
                    </motion.div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </FadeInWrapper>
      </div>

      {/* 버튼 영역 */}
      <FadeInWrapper className="px-5 py-4 w-full fixed bottom-0 left-0">
        <button
          onClick={handleRequestPermission}
          className={`w-full h-[60px] flex items-center justify-center rounded-3xl font-bold text-lg shadow-lg transition-colors ${
            permissionRequested
              ? "bg-yellow-300 text-black hover:bg-yellow-500"
              : "bg-[#1562fc] text-white hover:bg-blue-600"
          }`}
        >
          {permissionRequested ? "시작하기" : "알림 허용하기"}
        </button>
      </FadeInWrapper>
    </div>
  );
}
